import { useState, useMemo } from "react";
import type { UnitData, ActiveUser } from "../../../../store/public/publicTrackingLinkStore";
import { getTimeAgo } from "../../../../utils/getDate";
import {
  normalizeStatus,
  statusBadgeStyle,
  STATUS_COLOR,
  STATUS_FILTER_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
  type StatusCategory,
} from "./publicTrackingStatus";

interface PublicTrackingSidebarProps {
  units: UnitData[];
  activeUsers: ActiveUser[];
  linkName: string;
  selectedUnit: UnitData | null;
  onSelectUnit: (unit: UnitData | null) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
}

export function PublicTrackingSidebar({
  units,
  linkName,
  selectedUnit,
  onSelectUnit,
  isCollapsed,
  onToggleCollapse,
  selectedFilter,
  onFilterChange,
}: PublicTrackingSidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Asegurar que los datos sean arrays
  const safeUnits = Array.isArray(units) ? units : [];

  const themeClasses = {
    bg: "bg-dark-surface",
    border: "border-dark-border",
    text: "text-white",
    textSecondary: "text-gray-400",
    cardBg: "bg-dark-card",
    cardBorder: "border-dark-border",
    buttonHover: "hover:bg-dark-card",
    inputBg: "bg-dark-card",
    inputBorder: "border-dark-border",
  };

  const unitStats = useMemo(() => {
    const stats: Record<StatusCategory, number> = {
      driving: 0,
      ralenti: 0,
      stopped: 0,
      offline: 0,
    };
    safeUnits.forEach((unit) => {
      stats[normalizeStatus(unit.status)]++;
    });
    return stats;
  }, [safeUnits]);

  const totalUnits = safeUnits.filter(
    (unit) =>
      unit.latitude !== 0 &&
      unit.longitude !== 0 &&
      unit.latitude != null &&
      unit.longitude != null,
  ).length;

  const getStatusText = (status: string): string => STATUS_LABEL[normalizeStatus(status)];

  const filteredUnits = useMemo(() => {
    let filtered = safeUnits.filter(
      (unit) =>
        unit.latitude !== 0 &&
        unit.longitude !== 0 &&
        unit.latitude != null &&
        unit.longitude != null,
    );

    if (selectedFilter !== "all") {
      filtered = filtered.filter(
        (unit) => normalizeStatus(unit.status) === selectedFilter,
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (unit) =>
          unit.name.toLowerCase().includes(query) ||
          unit.address?.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [safeUnits, selectedFilter, searchQuery]);

  const formatLastReport = (lastReport: any): string => {
    if (!lastReport) return "Sin datos";
    let seconds: number;
    if (lastReport._seconds) {
      seconds = lastReport._seconds;
    } else if (typeof lastReport === "number") {
      seconds = Math.floor(lastReport / 1000);
    } else {
      seconds = Math.floor(new Date(lastReport).getTime() / 1000);
    }
    if (isNaN(seconds) || seconds <= 0) return "Sin datos";
    return getTimeAgo(seconds);
  };

  // Versión colapsada
  if (isCollapsed) {
    return (
      <div
        className={`w-16 ${themeClasses.bg} border-r ${themeClasses.border} flex flex-col h-full transition-all duration-300`}
      >
        <div
          className={`p-3 border-b ${themeClasses.border} flex flex-col items-center space-y-2`}
        >
          <button
            onClick={onToggleCollapse}
            className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
            title="Expandir sidebar"
          >
            <i className="ri-menu-unfold-line text-primary-500 text-lg"></i>
          </button>

          <div className="flex flex-col space-y-2">
            {STATUS_ORDER.map((category) => (
              <div
                key={category}
                className="flex flex-col items-center"
                title={`${STATUS_FILTER_LABEL[category]}: ${unitStats[category]} unidades`}
              >
                <div
                  className={`w-3 h-3 rounded-full ${category === "driving" ? "animate-pulse" : ""}`}
                  style={{ backgroundColor: STATUS_COLOR[category] }}
                ></div>
                <span className={`text-xs font-bold ${themeClasses.text} mt-1`}>
                  {unitStats[category]}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className={`text-2xl font-bold ${themeClasses.text} mb-1`}>
            {totalUnits}
          </div>
          <div className={`text-xs ${themeClasses.textSecondary} text-center`}>
            Total
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-[24rem] ${themeClasses.bg} border-r ${themeClasses.border} flex flex-col h-full transition-all duration-300`}
    >
      {/* Header */}
      <div className={`p-3 border-b ${themeClasses.border}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <i className="ri-map-pin-line text-gray-900 text-sm"></i>
            </div>
            <div>
              <h2 className={`text-sm font-bold ${themeClasses.text}`}>
                {linkName}
              </h2>
              <p className={`text-xs ${themeClasses.textSecondary}`}>
                Seguimiento en tiempo real
              </p>
            </div>
          </div>
          <button
            onClick={onToggleCollapse}
            className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
            title="Contraer sidebar"
          >
            <i className="ri-menu-fold-line text-primary-500 text-sm"></i>
          </button>
        </div>

        {/* Indicadores de estado - ahora clickeables */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {STATUS_ORDER.map((category) => {
            const active = selectedFilter === category;
            const color = STATUS_COLOR[category];
            return (
              <button
                key={category}
                onClick={() => onFilterChange(active ? "all" : category)}
                className={`p-2.5 ${themeClasses.cardBg} rounded-lg border transition-all ${
                  active
                    ? ""
                    : `${themeClasses.cardBorder} ${themeClasses.buttonHover}`
                }`}
                style={
                  active
                    ? { borderColor: color, backgroundColor: `${color}1A` }
                    : undefined
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-2.5 h-2.5 rounded-full ${category === "driving" ? "animate-pulse" : ""}`}
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>
                      {STATUS_FILTER_LABEL[category]}
                    </span>
                  </div>
                  <span className="text-base font-bold" style={{ color }}>
                    {unitStats[category]}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Buscador */}
        <div className="mb-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar unidad..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-9 pr-3 py-2 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.text} text-xs focus:outline-none focus:ring-1 focus:ring-primary-500`}
            />
          </div>
        </div>
      </div>

      {/* Lista de unidades */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {filteredUnits.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <i className="ri-truck-line text-4xl text-gray-500 mb-2"></i>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              No hay unidades que mostrar
            </p>
          </div>
        ) : (
          filteredUnits.map((unit) => (
            <div
              key={unit.id}
              onClick={() =>
                onSelectUnit(selectedUnit?.id === unit.id ? null : unit)
              }
              className={`p-3 ${themeClasses.cardBg} rounded-lg border ${
                selectedUnit?.id === unit.id
                  ? "border-primary-500"
                  : themeClasses.cardBorder
              } transition-all duration-200 hover:shadow-md cursor-pointer`}
            >
              {/* Header con nombre y estado */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <i className="ri-truck-line text-primary-500 text-base"></i>
                  <span
                    className={`font-mono font-bold ${themeClasses.text} text-xs`}
                  >
                    {unit.name}
                  </span>
                  {normalizeStatus(unit.status) === "driving" && (
                    <div
                      className="w-1.5 h-1.5 rounded-full animate-pulse"
                      style={{ backgroundColor: STATUS_COLOR.driving }}
                    ></div>
                  )}
                </div>
                <span
                  className="text-xs font-medium px-2 py-1 rounded-full"
                  style={statusBadgeStyle(unit.status)}
                >
                  {getStatusText(unit.status)}
                </span>
              </div>

              {/* Ubicación */}
              {unit.address && (
                <div className="flex items-start space-x-2 mb-2">
                  <i className="ri-map-pin-line text-gray-400 text-xs mt-0.5"></i>
                  <span
                    className={`${themeClasses.textSecondary} text-xs line-clamp-2`}
                  >
                    {unit.address}
                  </span>
                </div>
              )}

              {/* Indicadores principales */}
              <div className="grid grid-cols-3 gap-2">
                {/* Velocidad */}
                <div className="flex items-center space-x-1.5">
                  <i className="ri-speed-line text-blue-400 text-xs"></i>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>
                    {unit.speed || 0} km/h
                  </span>
                </div>

                {/* Último reporte */}
                <div className="flex items-center space-x-1.5">
                  <i className="ri-time-line text-purple-400 text-xs"></i>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>
                    {formatLastReport(unit.lastReport)}
                  </span>
                </div>

                {/* Kilometraje */}
                <div className="flex items-center space-x-1.5">
                  <i className="ri-roadster-line text-indigo-400 text-xs"></i>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>
                    {Math.round(unit.odometer || 0).toLocaleString("es-MX")} km
                  </span>
                </div>
              </div>

              {/* Coordenadas */}
              <div className="mt-2 pt-2 border-t border-gray-600">
                <div className="flex items-center space-x-1.5">
                  <i className="ri-map-2-line text-gray-400 text-xs"></i>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>
                    {unit.latitude?.toFixed(5)}, {unit.longitude?.toFixed(5)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div
        className={`p-3 border-t ${themeClasses.border} ${themeClasses.cardBg}`}
      >
        <div className="text-center">
          <p className={`text-xs ${themeClasses.text} font-medium`}>
            {`${filteredUnits.length} de ${totalUnits} Unidades`}
          </p>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {selectedFilter === "all"
              ? "Todas las Unidades"
              : STATUS_FILTER_LABEL[selectedFilter as StatusCategory] ||
                "Todas las Unidades"}
          </p>
        </div>
      </div>
    </div>
  );
}
